import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SplashScreen = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleAnimationComplete = () => {
    if (!isVisible) {
      onComplete();
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          data-testid="splash-screen"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          onAnimationComplete={handleAnimationComplete}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950 overflow-hidden px-4"
        >
          {/* Subtle grid background */}
          <div className="absolute inset-0">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.08 }}
              transition={{ duration: 1 }}
              className="absolute inset-0"
              style={{
                backgroundImage: `
                  linear-gradient(rgba(100, 130, 160, 0.2) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(100, 130, 160, 0.2) 1px, transparent 1px)
                `,
                backgroundSize: '40px 40px',
              }}
            />
          </div>

          {/* Radial glow */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5 }}
            className="absolute w-[300px] md:w-[600px] h-[300px] md:h-[600px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(100, 140, 180, 0.15) 0%, rgba(60, 80, 100, 0.05) 50%, transparent 70%)',
            }}
          />

          {/* Main content container */}
          <div className="relative z-10 flex flex-col items-center">
            {/* Logo Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="mb-6 md:mb-8"
            >
              <img
                src="/splash-logo.png"
                alt="Vrittaa AI"
                className="w-[180px] sm:w-[250px] md:w-[350px] lg:w-[400px] h-auto"
              />
            </motion.div>

            {/* Floating Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-center"
            >
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-xs sm:text-sm md:text-base tracking-[0.15em] sm:tracking-[0.2em] uppercase"
              >
                <span className="text-white font-semibold">VRITTAA AI</span>
                <span className="hidden sm:inline text-slate-600">|</span>
                <span className="text-slate-400">A WAAS COMPANY</span>
                <span className="hidden sm:inline text-slate-600">|</span>
                <span className="text-emerald-400">INTEGRATE AUTOMATION</span>
              </motion.div>
            </motion.div>

            {/* Loading bar */}
            <motion.div
              className="w-32 sm:w-40 md:w-48 h-0.5 mt-8 md:mt-10 rounded-full overflow-hidden bg-slate-800"
            >
              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: "0%" }}
                transition={{ duration: 2.8, ease: "linear" }}
                className="h-full w-full bg-gradient-to-r from-slate-500 via-emerald-400 to-slate-500"
              />
            </motion.div>
          </div>

          {/* Corner sparkle - hide on small screens */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="absolute bottom-10 right-10 hidden sm:block"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" fill="#94a3b8" opacity="0.5"/>
            </svg>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashScreen;
